import { formatMoney } from '@theme/money-formatting';
import { CartLinesUpdateEvent } from '@shopify/events';

const PLUS_ICON =
  '<svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M2.75 7H11.25" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/><path d="M7 2.75L7 11.25" stroke="currentColor" stroke-width="1.25" stroke-linecap="round"/></svg>';

const CHECK_ICON =
  '<svg aria-hidden="true" width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4.75439 10.7485L7.68601 14.5888C7.79288 14.7288 7.84632 14.7988 7.91174 14.8242C7.96907 14.8466 8.03262 14.8469 8.09022 14.8253C8.15596 14.8007 8.21026 14.7314 8.31886 14.5927L15.2475 5.74658" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round"/></svg>';

class CartCrossSell extends HTMLElement {
  #lastProductId = null;
  #observer = new MutationObserver(() => this.load());

  connectedCallback() {
    this.list = this.querySelector('.cart-cross-sell__list');
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-variant-id]');
      if (button) this.addToCart(button);
    });
    this.#observer.observe(this, { attributes: true, attributeFilter: ['data-product-id'] });
    this.load();
  }

  disconnectedCallback() {
    this.#observer.disconnect();
  }

  async load() {
    const productId = this.dataset.productId;
    if (!productId || productId === this.#lastProductId) return;
    this.#lastProductId = productId;

    try {
      const response = await fetch(`/recommendations/products.json?product_id=${productId}&limit=8&intent=related`);
      if (!response.ok) throw new Error(`Server returned ${response.status}`);
      const data = await response.json();
      const products = (data.products || []).filter((product) => product.available);

      if (!products.length) {
        this.hidden = true;
        return;
      }

      this.render(products);
      this.hidden = false;
    } catch (error) {
      console.error('[cart-cross-sell]', error);
      this.hidden = true;
    }
  }

  render(products) {
    const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    const currency = this.dataset.currency || 'USD';

    this.list.innerHTML = products
      .map((product) => {
        const variant = product.variants.find((v) => v.available) || product.variants[0];
        const price = formatMoney(product.price, moneyFormat, currency);
        const image = product.featured_image
          ? `<img src="${product.featured_image}&width=300" alt="" width="300" height="300" loading="lazy">`
          : '';

        return `
          <li class="cart-cross-sell__item">
            <a href="${product.url}" class="cart-cross-sell__link">
              <div class="cart-cross-sell__image">${image}</div>
              <p class="cart-cross-sell__title">${this.escape(product.title)}</p>
              <p class="cart-cross-sell__price">${price}</p>
            </a>
            ${
              variant?.available
                ? `<button type="button" class="cart-cross-sell__add" data-variant-id="${variant.id}" aria-label="${this.escape(product.title)}">
                    <span class="cart-cross-sell__add-icon">${PLUS_ICON}</span>
                    <span class="cart-cross-sell__add-icon cart-cross-sell__add-icon--check">${CHECK_ICON}</span>
                  </button>`
                : ''
            }
          </li>
        `;
      })
      .join('');
  }

  escape(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  async addToCart(button) {
    if (button.disabled) return;
    button.disabled = true;
    button.classList.add('is-loading');

    const variantId = Number(button.dataset.variantId);
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      const result = await response.json();

      if (result.status) {
        throw new Error(result.message || 'Add to cart failed');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'cart-cross-sell',
          sourceId: this.id || 'cart-cross-sell',
          itemCount: 1,
        },
      });

      button.classList.add('is-added');
      setTimeout(() => button.classList.remove('is-added'), 1500);
    } catch (error) {
      console.error('[cart-cross-sell]', error);
      deferred.reject(error instanceof Error ? error : new Error(String(error)));
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('cart-cross-sell')) {
  customElements.define('cart-cross-sell', CartCrossSell);
}
